import {
  HrApprovalsModel,
  HrAttendanceStatusModel,
  HrLeaveRequestModel,
  HrLeaveTypeModel,
  HrMeModel,
  HrPersonModel,
  HrWfhRequestModel
} from '../models/hr.model';
import {
  HrApprovalsUiModel,
  HrAttendanceUiModel,
  HrLeaveRequestUiModel,
  HrLeaveTypeUiModel,
  HrPersonUiModel,
  HrProfileUiModel,
  HrWfhRequestUiModel
} from '../ui-models/hr.ui-model';

const DEFAULT_LEAVE_COLOR = '#707071';

const date = (iso: string | null | undefined) => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

const num = (value: number | null | undefined) =>
  typeof value === 'number' && isFinite(value) ? value : 0;

/** `YYYY-MM-DD` from whatever Horilla sent (a date, or a timestamp). */
const day = (value: string | null | undefined) => (value ? String(value).slice(0, 10) : '');

function mapPerson(person: HrPersonModel | null | undefined): HrPersonUiModel | undefined {
  if (!person) return undefined;
  return { id: String(person.id), name: person.fullName || '', badgeId: person.badgeId || '' };
}

function mapProfile(model: HrMeModel | null | undefined): HrProfileUiModel {
  if (!model || !model.linked || !model.employee) return { linked: false, isManager: false };
  const employee = mapPerson(model.employee) as HrPersonUiModel;
  return {
    linked: true,
    employee: {
      ...employee,
      email: model.employee.email || '',
      imageUrl: model.employee.profileImageUrl || undefined
    },
    reportingManager: mapPerson(model.reportingManager),
    isManager: !!model.isManager
  };
}

function mapLeaveType(model: HrLeaveTypeModel): HrLeaveTypeUiModel {
  return {
    id: String(model.id),
    name: model.name || '',
    color: model.color || DEFAULT_LEAVE_COLOR,
    availableDays: num(model.availableDays),
    carryforwardDays: num(model.carryforwardDays),
    totalDays: num(model.totalDays),
    requireAttachment: !!model.requireAttachment
  };
}

function mapLeaveRequest(model: HrLeaveRequestModel): HrLeaveRequestUiModel {
  const leaveType = model.leaveType || { id: '', name: '' };
  return {
    id: String(model.id),
    leaveTypeId: String(leaveType.id),
    leaveTypeName: leaveType.name || '',
    leaveTypeColor: leaveType.color || DEFAULT_LEAVE_COLOR,
    startDate: day(model.startDate),
    startBreakdown: model.startDateBreakdown || 'full_day',
    endDate: day(model.endDate),
    endBreakdown: model.endDateBreakdown || 'full_day',
    days: typeof model.requestedDays === 'number' ? model.requestedDays : undefined,
    status: model.status,
    description: model.description || '',
    rejectReason: model.rejectReason || undefined,
    createdAt: date(model.createdAt),
    employee: mapPerson(model.employee)
  };
}

function mapWfhRequest(model: HrWfhRequestModel): HrWfhRequestUiModel {
  return {
    id: String(model.id),
    startDate: day(model.startDate),
    endDate: day(model.endDate),
    status: model.status,
    description: model.description || '',
    rejectReason: model.rejectReason || undefined,
    createdAt: date(model.createdAt),
    employee: mapPerson(model.employee)
  };
}

/** "HH:MM:SS" (or "HH:MM") to seconds; anything unparseable is 0. */
export function durationToSeconds(value: string | null | undefined): number {
  const match = /^(\d+):(\d{1,2})(?::(\d{1,2}))?/.exec(String(value || '').trim());
  if (!match) return 0;
  return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] || 0);
}

/**
 * The clock-in time as an instant. Horilla keeps it as a wall-clock time of today
 * — the BFF passes through Horilla's display string ("09:05 AM"), and a 24h
 * "09:12:00" or an ISO timestamp are accepted too.
 */
export function parseClockIn(value: string | null | undefined, now = new Date()): Date | undefined {
  if (!value) return undefined;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/.exec(value.trim());
  if (match) {
    let hours = Number(match[1]);
    const meridiem = (match[4] || '').toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    const at = new Date(now);
    at.setHours(hours, Number(match[2]), Number(match[3] || 0), 0);
    return at;
  }
  return date(value);
}

function mapAttendance(model: HrAttendanceStatusModel, now = new Date()): HrAttendanceUiModel {
  return {
    checkedIn: !!model.checkedIn,
    clockInAt: model.checkedIn ? parseClockIn(model.clockInTime, now) : undefined,
    workedSeconds: durationToSeconds(model.workedToday),
    fetchedAt: now
  };
}

function mapApprovals(model: HrApprovalsModel | null | undefined): HrApprovalsUiModel {
  return {
    leave: ((model && model.leave) || []).map(mapLeaveRequest),
    wfh: ((model && model.wfh) || []).map(mapWfhRequest)
  };
}

export default {
  mapProfile,
  mapLeaveType,
  mapLeaveRequest,
  mapWfhRequest,
  mapAttendance,
  mapApprovals
};
